import type { UseMutationResult } from 'react-query';
import { useMutation } from 'react-query';
import type { AxiosError } from 'axios';

import {
  RECORDING_HIGHLIGHTS_LIST_QUERY_KEY,
  HIGHLIGHTS_LIST_QUERY_KEY,
  UNSEEN_HIGHLIGHTS_COUNT,
  createRecordingHighlight,
} from '@hotjar/webapp/domains/highlights/api';
import * as collectionsApi from '@hotjar/webapp/domains/collections/api';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import type {
  RecordingHighlightCreateData,
  RecordingHighlight as RecordingHighlightModel,
} from '@hotjar/webapp/domains/highlights/recording/model';
import { mark, measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';

const RECORDING_HIGHLIGHT_CREATED = 'recording_highlight_created';

export function useRecordingHighlightCreate(
  siteId: string | null,
): UseMutationResult<RecordingHighlightModel, AxiosError, RecordingHighlightCreateData> {
  return useMutation(
    async (highlightData) => {
      if (siteId === null) {
        throw new Error('site ID is not defined');
      }

      // Perf: Mark create recording highlight timings
      mark(MARKS.RECORDING_HIGHLIGHT_CREATE_START);

      return createRecordingHighlight(siteId, {
        ...highlightData,
        uiPosition: highlightData.uiPosition ?? 'unknown',
      });
    },
    {
      onSuccess: () => {
        event(RECORDING_HIGHLIGHT_CREATED);

        queryClient.invalidateQueries(RECORDING_HIGHLIGHTS_LIST_QUERY_KEY);
        queryClient.invalidateQueries(HIGHLIGHTS_LIST_QUERY_KEY);
        queryClient.invalidateQueries(UNSEEN_HIGHLIGHTS_COUNT);
        queryClient.invalidateQueries(collectionsApi.COLLECTIONS_LIST_QUERY_KEY);
      },
      onSettled: () => {
        // Perf: Mark create recording highlight timings
        measureAndLog({
          name: MARKS.RECORDING_HIGHLIGHT_CREATE_TIME,
          startMark: MARKS.RECORDING_HIGHLIGHT_CREATE_START,
        });
      },
    },
  );
}
